/**
 * Browser sign-in with the company IdP (Okta) using OpenID Connect authorization code +
 * PKCE. No client secret exists in the browser. The ID token (audience = this app's client
 * ID) is sent as the bearer to /api/admin, where API Gateway's JWT authorizer verifies its
 * signature, issuer, audience and expiry before the request reaches the worker.
 */
export interface UiConfig {
  auth: 'sso' | 'dev' | 'unconfigured';
  api: string;
  issuer?: string;
  clientId?: string;
}

interface Discovery {
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint?: string;
}

const SESSION_KEY = 'acw.session';
const PKCE_KEY = 'acw.pkce';
/** Okta's sign-in redirect. Namespaced under /ui/auth/<provider>/ so other callbacks can sit beside it. */
export const OKTA_CALLBACK_PATH = '/ui/auth/okta/callback';
const redirectUri = () => `${location.origin}${OKTA_CALLBACK_PATH}`;

export interface Session {
  idToken: string;
  email: string;
  expiresAt: number;
}

function b64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(len = 48): string {
  return b64url(crypto.getRandomValues(new Uint8Array(len)));
}

async function sha256(text: string): Promise<string> {
  return b64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))));
}

function decodeJwt(token: string): Record<string, unknown> {
  const part = token.split('.')[1] ?? '';
  const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
  return JSON.parse(decodeURIComponent(escape(json))) as Record<string, unknown>;
}

async function discover(issuer: string): Promise<Discovery> {
  const res = await fetch(`${issuer}/.well-known/openid-configuration`);
  if (!res.ok) throw new Error(`Could not reach the sign-in provider (${res.status})`);
  return (await res.json()) as Discovery;
}

export function currentSession(): Session | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    return s.expiresAt - 30_000 > Date.now() ? s : null;
  } catch {
    return null;
  }
}

export function clearSession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* storage unavailable */
  }
}

export async function signIn(cfg: UiConfig): Promise<void> {
  const d = await discover(cfg.issuer!);
  const verifier = randomString();
  const state = randomString(16);
  const nonce = randomString(16);
  sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state, nonce, returnTo: location.hash || '#/' }));
  const params = new URLSearchParams({
    client_id: cfg.clientId!,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: redirectUri(),
    state,
    nonce,
    code_challenge: await sha256(verifier),
    code_challenge_method: 'S256',
  });
  location.assign(`${d.authorization_endpoint}?${params}`);
}

/** Completes the redirect back from the IdP. Returns the hash route to show, or throws. */
export async function completeSignIn(cfg: UiConfig): Promise<string> {
  const q = new URLSearchParams(location.search);
  if (q.get('error')) throw new Error(q.get('error_description') ?? q.get('error') ?? 'Sign-in failed');
  const pending = JSON.parse(sessionStorage.getItem(PKCE_KEY) ?? 'null') as {
    verifier: string;
    state: string;
    nonce: string;
    returnTo: string;
  } | null;
  sessionStorage.removeItem(PKCE_KEY);
  if (!pending || q.get('state') !== pending.state) throw new Error('Sign-in expired or was tampered with. Please try again.');
  const d = await discover(cfg.issuer!);
  const res = await fetch(d.token_endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded', accept: 'application/json' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: cfg.clientId!,
      code: q.get('code') ?? '',
      redirect_uri: redirectUri(),
      code_verifier: pending.verifier,
    }),
  });
  if (!res.ok) throw new Error(`Sign-in could not be completed (${res.status})`);
  const tokens = (await res.json()) as { id_token?: string };
  if (!tokens.id_token) throw new Error('The sign-in provider returned no ID token');
  const claims = decodeJwt(tokens.id_token);
  if (claims.nonce !== pending.nonce) throw new Error('Sign-in response did not match this request');
  const email = String(claims.email ?? claims.preferred_username ?? '');
  sessionStorage.setItem(SESSION_KEY, JSON.stringify({ idToken: tokens.id_token, email, expiresAt: Number(claims.exp) * 1000 }));
  return pending.returnTo;
}

export async function signOut(cfg: UiConfig): Promise<void> {
  const s = currentSession();
  clearSession();
  if (cfg.auth !== 'sso' || !cfg.issuer) return location.assign('/ui/');
  try {
    const d = await discover(cfg.issuer);
    if (d.end_session_endpoint && s) {
      const p = new URLSearchParams({ id_token_hint: s.idToken, post_logout_redirect_uri: `${location.origin}/ui/` });
      return location.assign(`${d.end_session_endpoint}?${p}`);
    }
  } catch {
    /* fall through */
  }
  location.assign('/ui/');
}
